import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ChevronDownIcon,
  CheckIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import {
  buildMidusPickerRows,
  resolveEffectiveDefaultModelId,
  patchDefaultChatModelKey,
  mergeUserFromPatch,
  persistChatInputModelSelection,
} from '../../utils/defaultChatModel';
import './HeaderModelSelector.css';

function selectionMatchesRow(row, provider, modelId) {
  return row.provider === provider && row.model === modelId;
}

const getApiUrl = (endpoint) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081';
  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  if (baseUrl.endsWith('/api')) {
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
    return `${baseUrl}/${cleanEndpoint}`;
  }
  const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
  return `${baseUrl}/${cleanEndpoint}`;
};

const getModelDescription = (displayName) => {
  if (!displayName) return '';
  const lower = displayName.toLowerCase();
  if (lower.includes('max')) return 'High-performance agent designed for complex tasks. Can consume usage quickly.';
  if (lower.includes('lite') || lower.includes('fast')) return 'A lightweight agent for everyday tasks.';
  return 'Versatile agent capable of most tasks.';
};

export function HeaderModelSelector({ selectedModel, onModelSelect, disabled }) {
  const { user, setUser } = useAuthContext();
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuMode, setMenuMode] = useState('session');
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const dropdownRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const selectedProvider = selectedModel?.provider;
  const selectedModelId = selectedModel?.id;

  const pickerRows = useMemo(() => buildMidusPickerRows(availableModels), [availableModels]);

  const effectiveDefaultModelId = useMemo(
    () => resolveEffectiveDefaultModelId(pickerRows, user?.preferences?.defaultChatModelKey),
    [pickerRows, user?.preferences?.defaultChatModelKey]
  );

  /** Legacy `auto` / empty parent while hydrating → treat as effective default for UI. */
  const resolvedRowId = useMemo(() => {
    const legacyAuto =
      selectedProvider === 'auto' && selectedModelId === 'auto';
    const missing =
      selectedProvider == null ||
      selectedModelId == null ||
      selectedProvider === '' ||
      selectedModelId === '';
    if (legacyAuto || missing) return effectiveDefaultModelId;
    return `${selectedProvider}:${selectedModelId}`;
  }, [selectedProvider, selectedModelId, effectiveDefaultModelId]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
          const response = await axios.get(getApiUrl('admin/models'), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (Array.isArray(response.data) && response.data.length > 0) {
            setAvailableModels(response.data);
          }
        } catch {
          const fallback = await axios.get(getApiUrl('models/default'));
          if (Array.isArray(fallback.data) && fallback.data.length > 0) {
            setAvailableModels(fallback.data);
          }
        }
      } catch (err) {
        console.error('HeaderModelSelector: Failed to fetch models', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) {
      setMenuMode('session');
    }
  }, [isDropdownOpen]);

  const handleSelect = (provider, model) => {
    onModelSelect?.({ provider, id: model });
    setIsDropdownOpen(false);
  };

  const handlePersistDefault = useCallback(
    async (row) => {
      if (!setUser || isSavingDefault || !row) return;
      try {
        setIsSavingDefault(true);
        const data = await patchDefaultChatModelKey(row.id);
        const merged = mergeUserFromPatch(user, data);
        setUser(merged);
        persistChatInputModelSelection(row.provider, row.model);
        onModelSelect?.({ provider: row.provider, id: row.model });
        setIsDropdownOpen(false);
        setMenuMode('session');
      } catch (err) {
        console.error('HeaderModelSelector: Failed to save default model', err);
      } finally {
        setIsSavingDefault(false);
      }
    },
    [user, setUser, isSavingDefault, onModelSelect]
  );

  const displayLabel = useMemo(() => {
    if (isLoading) return 'Loading...';
    const byId = resolvedRowId ? pickerRows.find((r) => r.id === resolvedRowId) : null;
    if (byId?.displayName) return byId.displayName;
    const modelData = availableModels.find(
      (m) => m.provider === selectedProvider && m.model === selectedModelId
    );
    return modelData?.displayName || pickerRows[0]?.displayName || 'Model';
  }, [
    isLoading,
    resolvedRowId,
    pickerRows,
    availableModels,
    selectedProvider,
    selectedModelId,
  ]);

  if (!selectedModel && isLoading) return null;

  const sessionPicker = (
    <>
      <p className="px-3 pt-0.5 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Models
      </p>
      {pickerRows.map((row) => {
        const isSelected =
          resolvedRowId === row.id ||
          selectionMatchesRow(row, selectedProvider, selectedModelId);
        return (
          <button
            key={row.id}
            type="button"
            className="header-model-selector-item w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
            onClick={() => handleSelect(row.provider, row.model)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="text-[15px] font-semibold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  {row.displayName}
                </span>
              </div>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                {getModelDescription(row.displayName)}
              </p>
            </div>
            {isSelected && (
              <CheckIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
            )}
          </button>
        );
      })}

      <div className="my-1 border-t border-gray-200 dark:border-[#2a2a2a]" />

      <button
        type="button"
        className="header-model-selector-item w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
        onClick={() => setMenuMode('default')}
      >
        <ArrowPathIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span
          className="text-[15px] font-semibold text-gray-900 dark:text-white"
          style={{ fontFamily: 'var(--chat-font-family)' }}
        >
          Change default
        </span>
      </button>
    </>
  );

  const defaultPicker = (
    <>
      <button
        type="button"
        className="header-model-selector-item w-full text-left px-3 py-2.5 flex items-center gap-2 transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
        onClick={() => setMenuMode('session')}
      >
        <ChevronLeftIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span
          className="text-[15px] font-semibold text-gray-900 dark:text-white"
          style={{ fontFamily: 'var(--chat-font-family)' }}
        >
          Back
        </span>
      </button>
      <p className="px-3 pt-1 pb-2 text-[12px] text-gray-500 dark:text-gray-400">
        Default model for new chats
      </p>
      {pickerRows.map((row) => {
        const isChecked = row.id === effectiveDefaultModelId;
        return (
          <button
            key={row.id}
            type="button"
            disabled={isSavingDefault}
            className="header-model-selector-item w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e] disabled:opacity-50"
            onClick={() => handlePersistDefault(row)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="text-[15px] font-semibold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  {row.displayName}
                </span>
                {/* row.isPro && (
                  <span className="px-1.5 py-0.5 text-[11px] font-semibold bg-[#FBBAA0]/20 dark:bg-[#FBBAA0]/15 text-[#B85A3A] dark:text-[#FBBAA0] rounded">
                    Pro
                  </span>
                ) */}
              </div>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                {getModelDescription(row.displayName)}
              </p>
            </div>
            {isChecked && (
              <CheckIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
            )}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
        disabled={disabled}
        className="header-model-selector-btn flex items-center px-3 py-1.5 text-md text-[#444444] dark:text-[#aaaaaa] hover:text-[#181818] dark:hover:text-gray-200 hover:bg-[#ededed] dark:hover:bg-[#282828] transition-colors border border-transparent hover:border-[#dbdbdb] dark:hover:border-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="header-model-rolodex inline-flex items-center align-middle">
          {prefersReducedMotion ? (
            <span style={{ fontFamily: 'var(--chat-font-family)', fontWeight: 600 }}>{displayLabel}</span>
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={displayLabel}
                initial={{ rotateX: -88, opacity: 0, y: 6, filter: 'blur(3px)' }}
                animate={{ rotateX: 0, opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ rotateX: 88, opacity: 0, y: -6, filter: 'blur(3px)' }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  fontFamily: 'var(--chat-font-family)',
                  fontWeight: 600,
                  transformOrigin: 'center center',
                  display: 'inline-block',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                {displayLabel}
              </motion.span>
            </AnimatePresence>
          )}
        </span>
        <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
      </button>

      {isDropdownOpen && (
        <div className="header-model-selector-dropdown absolute top-full left-0 mt-2 w-80 bg-white dark:bg-[#161616] shadow-lg border border-gray-200 dark:border-[#2a2a2a] py-1.5 px-1.5 z-[100] max-h-[min(70vh,520px)] overflow-y-auto">
          {menuMode === 'session' ? sessionPicker : defaultPicker}
        </div>
      )}
    </div>
  );
}
